import { Injectable, inject } from "@angular/core";
import { lastValueFrom, Observable, throwError } from "rxjs";
import { catchError, map, } from "rxjs/operators";

import { DispatchService, IDispatchCriteria } from "./dispatch.service";
import { getLoggerFor, LogAs, LogInvocation, LogLevel } from "@tylertech/logging";

@Injectable()
export class ApiService
{
    private readonly $dispatchService = inject(DispatchService);

    private readonly log = getLoggerFor("ApiService");

    @LogInvocation(LogLevel.DEBUG, true)
    private _send<TRequest, TResponse>(
        @LogAs((p: IDispatchCriteria<unknown>) => `[DispatchCriteria "${p.Endpoint}"]`) criteria: IDispatchCriteria<TRequest>,
        preAuth: boolean): Observable<TResponse>
    {
        return this.$dispatchService.dispatch<TRequest, TResponse>(criteria, preAuth)
            .pipe(
                map((response) =>
                {
                    if (!isServiceException(response.body))
                    {
                        return response.body;
                    }

                    throw new Error(`Unexpected Response: Data was: ${JSON.stringify(response)}`);
                }),
                catchError((error: unknown) =>
                {
                    this.log.error(`Unexpected value returned. value was ${JSON.stringify(error)}`);

                    return throwError(() => error);
                }));

        function isServiceException(obj: unknown): boolean
        {
            return !!obj
                && typeof obj === "object"
                && "StackTrace" in obj;
        }
    }

    @LogInvocation(LogLevel.DEBUG, true)
    sendAuth<TRequest, TResponse>(endpoint: string, body?: TRequest, filterString?: string): Promise<TResponse>
    {
        const criteria: IDispatchCriteria<TRequest> = {
            Endpoint: endpoint,
            Model: body,
            FilterString: filterString,
        };
        const requestObservable = this._send<TRequest, TResponse>(criteria, false);
        return lastValueFrom(requestObservable);
    }

    @LogInvocation(LogLevel.DEBUG, true)
    sendUnauth<TRequest, TResponse>(endpoint: string, body?: TRequest, filterString?: string): Promise<TResponse>
    {
        const criteria: IDispatchCriteria<TRequest> = {
            Endpoint: endpoint,
            Model: body,
            FilterString: filterString,
        };
        const requestObservable = this._send<TRequest, TResponse>(criteria, true);
        return lastValueFrom(requestObservable);
    }
}
